import NiceModal from '@ebay/nice-modal-react';
import { ActionIcon, Badge, Flex, Grid, Loader, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconMessage } from '@tabler/icons-react';

import { InstPage, Platform, TgPage } from '../../../types';

import { IgTokenStatus } from './Buttons/IgTokenStatus';
import { InstIntegration } from './Buttons/InstIntegration';
import { RefreshToken } from './Buttons/RefreshToken';
import { UnlockButton } from './Buttons/UnlockButton';
import { ControlsPanel } from './ControlsPanel';
import { PostsModal } from './Modals/PostsModal';

type PageCardProps = {
  page: TgPage | InstPage;
  platform: Platform;
  isBlocked?: boolean;
};

export const PageCard = ({ page, platform, isBlocked = false }: PageCardProps) => {
  const isInstagramPage = 'inst' in page;
  const isRestoring = page.config.isRestoring;
  const isInstLoginPage = isInstagramPage && (page as InstPage).integration === 'instagram-connect';
  const isMissingDepartment = isInstagramPage && !(page as InstPage).department;
  // Refreshing needs nothing but `inst.fbId`. Instagram Login pages run on an IG User Token
  // instead, and Umnico pages are created without an fbId, so both are excluded by this check
  // on their own — no need to look at the legacy `umnico` subdocument, which survives a
  // migration to Instagram and would keep hiding the button on a page that can refresh.
  const isCanRefreshFbToken = isInstagramPage && !!(page as InstPage).inst.fbId && !isInstLoginPage;

  return (
    <Grid.Col key={page._id} span={1}>
      <Paper radius="md" p="md" style={isRestoring ? { opacity: 0.7 } : undefined}>
        <Flex direction="column" rowGap="xs">
          <Stack gap="xs">
            <Flex direction="row" justify="space-between" align="center">
              <Flex align="center" gap="xs">
                <Text size="sm">{page.name + (page.username ? ` (${page.username})` : '')}</Text>
                {isRestoring && (
                  <Tooltip label="Data is being restored from archive. Please wait...">
                    <Badge
                      color="orange"
                      size="sm"
                      leftSection={<Loader size={10} color="orange" />}
                    >
                      Restoring...
                    </Badge>
                  </Tooltip>
                )}
                {isMissingDepartment && (
                  <Tooltip label="Department is not set. Please edit the page.">
                    <IconAlertCircle size={16} color="var(--mantine-color-yellow-5)" />
                  </Tooltip>
                )}
              </Flex>
              <Flex gap={5} align="center">
                {isInstagramPage && (
                  <>
                    <Tooltip label="View comments">
                      <ActionIcon
                        size="sm"
                        variant="filled"
                        radius="lg"
                        onClick={() =>
                          NiceModal.show(PostsModal, {
                            pageId: page._id,
                            pageName: page.name,
                          })
                        }
                      >
                        <IconMessage size={16} />
                      </ActionIcon>
                    </Tooltip>
                    {isBlocked && <UnlockButton page={page} />}
                    {isCanRefreshFbToken && <RefreshToken page={page} />}
                    <InstIntegration page={page} />
                  </>
                )}
              </Flex>
            </Flex>
            {'phone' in page && (
              <Text opacity={0.5} size="xs">
                {page.phone}
              </Text>
            )}
            {isInstagramPage && (
              <Text opacity={0.5} size="xs">
                {page.inst.id}
              </Text>
            )}
            {isInstLoginPage && <IgTokenStatus page={page as InstPage} />}
          </Stack>
          <ControlsPanel page={page} platform={platform} />
        </Flex>
      </Paper>
    </Grid.Col>
  );
};
